// v1.28.23 — Compact "Top Parties of the Month" widget for the admin
// dashboard. Fetches /api/reports/top-parties (scoped db → tenant
// isolation is automatic). Every Company Admin sees only their own
// parties; a Super User in an impersonation context sees just that
// tenant's list.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, ArrowRight } from "@phosphor-icons/react";
import api from "@/lib/api";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n || 0));

export default function TopPartiesWidget() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [month, setMonth] = useState("");
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/reports/top-parties", { params: { sort: "amount", limit: 3 } });
                setRows(data.rows || []);
                const dt = new Date(data.year, data.month - 1, 1);
                setMonth(dt.toLocaleString("en-IN", { month: "long", year: "numeric" }));
            } catch (_e) { /* silent */ }
            finally { setLoading(false); }
        })();
    }, []);
    return (
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="top-parties-widget">
            <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
                <div className="flex items-center gap-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow">
                        <Trophy size={16} weight="fill" />
                    </span>
                    <div>
                        <h3 className="font-display text-sm font-bold text-slate-900">Top Parties of the Month</h3>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">{month || "This month"} · top 3 by amount</div>
                    </div>
                </div>
                <Link to="/admin/reports/top-parties" data-testid="top-parties-widget-view-all" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-amber-700 hover:text-amber-800">
                    View full report <ArrowRight size={12} weight="bold" />
                </Link>
            </div>
            {loading ? (
                <div className="px-3 py-5 text-center text-xs text-slate-400">Loading…</div>
            ) : rows.length === 0 ? (
                <div className="px-3 py-5 text-center text-xs text-slate-400">No transactions this month yet.</div>
            ) : (
                <ol className="divide-y divide-slate-100">
                    {rows.slice(0, 3).map((r) => (
                        <li key={r.party_id} data-testid={`top-parties-widget-row-${r.party_id}`} className="flex items-center gap-2 px-3 py-1.5">
                            <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-black ${
                                r.rank === 1 ? "bg-amber-100 text-amber-700"
                                : r.rank === 2 ? "bg-slate-100 text-slate-700"
                                : r.rank === 3 ? "bg-orange-100 text-orange-700"
                                : "bg-slate-50 text-slate-500"
                            }`}>{r.rank}</span>
                            <div className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">{r.party_name}</div>
                            <div className="shrink-0 text-sm font-bold text-emerald-700 tabular-nums">{inr(r.total_received)}</div>
                        </li>
                    ))}
                </ol>
            )}
            {!loading && rows.length > 0 && (
                <Link to="/admin/reports/top-parties" data-testid="top-parties-widget-view-all-bottom" className="flex items-center justify-center gap-1 border-t border-slate-100 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-amber-700 hover:bg-amber-50 hover:text-amber-800">
                    View All <ArrowRight size={12} weight="bold" />
                </Link>
            )}
        </div>
    );
}
